import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle, XCircle, Loader2 } from "lucide-react";

interface RequestInfo {
  request_number: string;
  category: string;
  company: string | null;
  created_at: string;
}

const AnfrageZurueckziehen = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<"loading" | "ready" | "submitting" | "success" | "error" | "already_withdrawn">("loading");
  const [requestInfo, setRequestInfo] = useState<RequestInfo | null>(null);
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [cancelledCount, setCancelledCount] = useState(0);

  useEffect(() => {
    if (!token) { setStatus("error"); setErrorMessage(t("withdraw.noToken")); return; }

    const validate = async () => {
      const { data, error } = await supabase.functions.invoke("withdraw-request", {
        body: { action: "validate", token },
      });

      if (error || data?.error) {
        if (data?.already_withdrawn) {
          setStatus("already_withdrawn");
        } else {
          setStatus("error");
          setErrorMessage(data?.error || "Ungültiger Link");
        }
        return;
      }

      setRequestInfo(data);
      setStatus("ready");
    };

    validate();
  }, [token]);

  const handleWithdraw = async () => {
    if (reason.trim().length < 5) return;
    setStatus("submitting");

    const { data, error } = await supabase.functions.invoke("withdraw-request", {
      body: { action: "withdraw", token, reason: reason.trim() },
    });

    if (error || data?.error) {
      if (data?.already_withdrawn) {
        setStatus("already_withdrawn");
      } else {
        setStatus("error");
        setErrorMessage(data?.error || "Fehler beim Zurückziehen");
      }
      return;
    }

    setCancelledCount(data?.cancelled_opportunities || 0);
    setStatus("success");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title={t("withdraw.seoTitle")}
        description={t("withdraw.seoDesc")}
        canonical="/anfrage-zurueckziehen"
      />
      <Header />

      <main id="main-content" className="flex-1 pt-28 pb-20 section-padding flex items-center">
        <div className="max-w-xl mx-auto w-full">

          {status === "loading" && (
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-caption">{t("withdraw.loading")}</p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-destructive/10 mb-8">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-headline mb-4">{t("withdraw.errorTitle")}</h1>
              <p className="text-caption">{errorMessage}</p>
            </div>
          )}

          {status === "already_withdrawn" && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-8">
                <CheckCircle className="w-10 h-10 text-muted-foreground" />
              </div>
              <h1 className="text-headline mb-4">{t("withdraw.alreadyTitle")}</h1>
              <p className="text-caption">{t("withdraw.alreadyDesc")}</p>
            </div>
          )}

          {status === "ready" && requestInfo && (
            <div className="space-y-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-8">
                  <AlertTriangle className="w-10 h-10 text-accent" />
                </div>
                <h1 className="text-headline mb-4">{t("withdraw.title")}</h1>
                <p className="text-caption">{t("withdraw.confirmQuestion")}</p>
              </div>

              <div className="border border-border bg-card p-6 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-micro">{t("withdraw.requestNumber")}</span>
                    <p className="text-body mt-1">{requestInfo.request_number}</p>
                  </div>
                  <div>
                    <span className="text-micro">{t("withdraw.category")}</span>
                    <p className="text-body mt-1">{requestInfo.category}</p>
                  </div>
                  {requestInfo.company && (
                    <div>
                      <span className="text-micro">{t("withdraw.company")}</span>
                      <p className="text-body mt-1">{requestInfo.company}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-micro">{t("withdraw.createdAt")}</span>
                    <p className="text-body mt-1">
                      {new Date(requestInfo.created_at).toLocaleDateString("de-DE")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-micro block">
                  {t("withdraw.reasonLabel")} <span className="text-destructive">*</span>
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("withdraw.reasonPlaceholder")}
                  rows={4}
                  className="w-full"
                />
                {reason.length > 0 && reason.trim().length < 5 && (
                  <p className="text-xs text-destructive">{t("withdraw.reasonMin")}</p>
                )}
              </div>

              <div className="p-4 bg-destructive/5 border border-destructive/20">
                <p className="text-caption text-sm">
                  <strong>{t("common.error") === "Fehler" ? "Hinweis" : "Note"}:</strong> {t("withdraw.warning")}
                </p>
              </div>

              <Button
                variant="destructive"
                className="w-full"
                onClick={handleWithdraw}
                disabled={reason.trim().length < 5}
              >
                {t("withdraw.submitBtn")}
              </Button>
            </div>
          )}

          {status === "submitting" && (
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-caption">{t("withdraw.submitting")}</p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-8">
                <CheckCircle className="w-10 h-10 text-accent" />
              </div>
              <h1 className="text-headline mb-4">{t("withdraw.successTitle")}</h1>
              <p className="text-caption mb-2">
                {t("withdraw.successDesc")}
              </p>
              {cancelledCount > 0 && (
                <p className="text-caption text-muted-foreground">
                  {t(cancelledCount === 1 ? "withdraw.notifiedOne" : "withdraw.notifiedMany", { count: cancelledCount })}
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnfrageZurueckziehen;
